import React from 'react';
import moment from 'moment';

// Dokumen surat Permohonan PLP untuk cetak PDF via react-to-print.
// Mapping row tabel -> PDF (mendukung bentuk flat baru & nested header/detil lama):
//   nomorSurat + kodeTpsTujuan + tahun(tanggalSurat) -> Nomor surat (cth: 074476/CEN1/PLP/2026)
//   nomorBc11, tanggalBc11                           -> No.BC11 + Tanggal
//   kemasan[] { jenisKemasan, jumlahKemasan, nomorBlAwb, tanggalBlAwb } -> tabel tengah
//   gudangAsal/gudangTujuan (+nama dari warehouseCache) -> TPS asal/tujuan
//   tpsTujuan.alasanPenunjukan                       -> Alasan
//   response.data.nomorPlp/tanggalPlp                -> Nomor/Tanggal keputusan
//   namaPemohon                                      -> Pemohon
// CATATAN: NO POS dan berat kemasan tidak ada di payload API -> tampil '-'.
// Kolom "Keputusan Pejabat BC" diisi free text "SETUJU".
// Box bawah (Pukul/Segel/Nama/NIP/TTD) sengaja dikosongkan untuk diisi manual.

const pick = (row, flat, nested) => row?.[flat] ?? row?.header?.[nested] ?? row?.[nested];
const getKemasan = (row) => row?.kemasan || row?.detil?.[0]?.kemasan || [];

const toMoment = (v) => {
    if (!v) return null;
    if (moment.isMoment(v)) return v.isValid() ? v : null;
    const m = moment(v, ['DD-MM-YYYY', 'DD-MMM-YYYY', 'YYYYMMDD', moment.ISO_8601], true);
    return m.isValid() ? m : null;
};
const str = (v) => (v == null || v === '' ? '-' : String(v));

// Cari nama perusahaan TPS dari warehouseCache (by kode TPS, fallback by kode gudang)
const findTpsName = (warehouses, kodeTps, kodeGudang) => {
    const list = warehouses || [];
    const byTps = kodeTps && list.find((w) => w.kode_tps === kodeTps);
    if (byTps?.nama) return byTps.nama;
    const byGudang = kodeGudang && list.find((w) => w.kode_warehouse === kodeGudang);
    if (byGudang?.nama) return byGudang.nama;
    return kodeGudang || kodeTps || '-';
};

const th = { border: '1px solid #000', padding: 6, fontWeight: 'bold', textAlign: 'center' };
const td = { border: '1px solid #000', padding: 6, textAlign: 'center', verticalAlign: 'top' };

const PrintPlpDocument = React.forwardRef(({ data, warehouses }, ref) => {
    if (!data) return <div ref={ref} />;

    const nomorSurat = pick(data, 'nomorSurat', 'nomorSurat');
    const kodeTpsTujuan = pick(data, 'kodeTpsTujuan', 'kodeTpsTujuan');
    const tahunSurat = toMoment(pick(data, 'tanggalSurat', 'tanggalSurat'))?.format('YYYY') || '....';
    const nomorBc11 = pick(data, 'nomorBc11', 'nomorBc11');
    const tanggalBc11 = pick(data, 'tanggalBc11', 'tanggalBc11');
    const kemasan = getKemasan(data);
    const gudangAsal = pick(data, 'gudangAsal', 'gudangAsal');
    const gudangTujuan = pick(data, 'gudangTujuan', 'gudangTujuan');
    const namaAsal = findTpsName(warehouses, pick(data, 'kodeTpsAsal', 'kodeTpsAsal'), gudangAsal);
    const namaTujuan = findTpsName(warehouses, kodeTpsTujuan, gudangTujuan);
    const alasan = data?.tpsTujuan?.alasanPenunjukan || data?.header?.tpsTujuan?.alasanPenunjukan
        || pick(data, 'alasanPenunjukan', 'alasanPenunjukan');
    const nomorPlp = data?.response?.data?.nomorPlp;
    const tanggalPlp = data?.response?.data?.tanggalPlp;
    const namaPemohon = pick(data, 'namaPemohon', 'namaPemohon');
    const nomorPosHeader = pick(data, 'nomorPos', 'nomorPos') || data?.nomor_pos || data?.header?.nomor_pos
        || pick(data, 'noPos', 'noPos') || data?.no_pos || data?.header?.no_pos;
    const tglBox = toMoment(tanggalPlp)?.format('DD MMMM YYYY') || moment().format('DD MMMM YYYY');

    return (
        <div ref={ref} style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: 12, color: '#000', lineHeight: 1.5 }}>
            <table style={{ borderCollapse: 'collapse' }}>
                <tbody>
                    <tr><td style={{ width: 70 }}>Nomor</td><td style={{ width: 10 }}>:</td><td style={{ fontWeight: 'bold' }}>{str(nomorSurat)}/{str(kodeTpsTujuan)}/PLP/{tahunSurat}</td></tr>
                    <tr><td>Lampiran</td><td>:</td><td>Copy AWB DO Manifest Surat Pernyataan</td></tr>
                    <tr><td>Hal</td><td>:</td><td>Permohonan Pindah Lokasi Penimbunan (PLP)</td></tr>
                </tbody>
            </table>

            <p style={{ marginTop: 16 }}>Yth. Kepala KPU Beacukai Tipe C Soekarno Hatta<br />u.p Kepala bidang pelayanan dan fasilitas pabean dan cukai II</p>

            <p style={{ textAlign: 'justify' }}>
                Dengan ini kami mengajukan permohonan Pemindahan Lokasi Penimbunan (PLP)
                atas barang import yang belum diselesaikan kewajiban pabeannya sebagai berikut:
            </p>

            <table style={{ borderCollapse: 'collapse', marginBottom: 8 }}>
                <tbody>
                    <tr>
                        <td style={{ width: 70 }}>No.BC11</td><td style={{ width: 10 }}>:</td>
                        <td style={{ width: 180 }}>{str(nomorBc11)}</td>
                        <td style={{ width: 90 }}>Tanggal :</td><td>{str(tanggalBc11)}</td>
                    </tr>
                </tbody>
            </table>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th rowSpan={2} style={{ ...th, width: '14%' }}>NO POS</th>
                        <th colSpan={2} style={th}>KEMASAN</th>
                        <th colSpan={2} style={th}>DOKUMEN AWB</th>
                        <th rowSpan={2} style={{ ...th, width: '18%' }}>Keputusan Pejabat BC</th>
                    </tr>
                    <tr>
                        <th style={th}>Jenis</th><th style={th}>Jumlah</th>
                        <th style={th}>Nomor</th><th style={th}>Tanggal</th>
                    </tr>
                </thead>
                <tbody>
                    {(kemasan.length ? kemasan : [{}]).map((k, i) => (
                        <tr key={i}>
                            <td style={td}>{k.nomorPos || k.noPos || nomorPosHeader || ''}</td>
                            <td style={td}>{str(k.jenisKemasan)}</td>
                            <td style={td}>{k.jumlahKemasan ?? '-'}</td>
                            <td style={td}>{str(k.nomorBlAwb)}</td>
                            <td style={td}>{toMoment(k.tanggalBlAwb)?.format('DD MMM YYYY') || '-'}</td>
                            <td style={td}>SETUJU</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <table style={{ borderCollapse: 'collapse', marginTop: 8 }}>
                <tbody>
                    <tr><td style={{ width: 140 }}>TPS asal/GUDANG</td><td style={{ width: 10 }}>:</td><td style={{ width: 280 }}>{str(namaAsal)}</td><td>Kode GUDANG :</td><td>{str(gudangAsal)}</td></tr>
                    <tr><td>TPS tujuan/GUDANG</td><td>:</td><td>{str(namaTujuan)}</td><td>Kode GUDANG :</td><td>{str(gudangTujuan)}</td></tr>
                    <tr><td>Alasan</td><td>:</td><td colSpan={3}>{str(alasan)}</td></tr>
                </tbody>
            </table>

            <p style={{ marginTop: 16 }}>Demikian kami sampaikan agar dapat dipertimbangkan</p>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                <tbody>
                    <tr>
                        <td style={{ width: '55%', verticalAlign: 'top' }}>
                            Keputusan pejawab Bea dan Cukai :<br />
                            Nomor&ensp;&ensp;: {str(nomorPlp)}<br />
                            Tanggal : {str(tanggalPlp)}
                        </td>
                        <td style={{ verticalAlign: 'top' }}>
                            Pemohon<br />{str(namaPemohon)}
                        </td>
                    </tr>
                </tbody>
            </table>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 24 }}>
                <tbody>
                    <tr>
                        <td style={{ width: '48%', border: '1px solid #000', padding: 8, verticalAlign: 'top' }}>
                            Pengeluaran dari TPS asal<br /><br />
                            Tanggal&ensp;&ensp;&ensp;: {tglBox}<br />
                            Pukul&ensp;&ensp;&ensp;&ensp;&ensp;&ensp;: <br />
                            Nomor Segel&ensp;: <br /><br />
                            Pejabat Bea dan Cukai<br />
                            Nama&ensp;&ensp;&ensp;&ensp;&ensp;: <br />
                            NIP&ensp;&ensp;&ensp;&ensp;&ensp;&ensp;&ensp;: <br />
                            Tanda Tangan :
                            <div style={{ height: 40 }}>&nbsp;</div>
                        </td>
                        <td style={{ width: '4%', border: 'none' }} />
                        <td style={{ border: '1px solid #000', padding: 8, verticalAlign: 'top' }}>
                            Pemasukan ke TPS tujuan<br /><br />
                            Tanggal&ensp;&ensp;&ensp;: {tglBox}<br />
                            Pukul&ensp;&ensp;&ensp;&ensp;&ensp;&ensp;: <br />
                            Nomor Segel&ensp;: <br /><br />
                            Pejabat Bea dan Cukai<br />
                            Nama&ensp;&ensp;&ensp;&ensp;&ensp;: <br />
                            NIP&ensp;&ensp;&ensp;&ensp;&ensp;&ensp;&ensp;: <br />
                            Tanda Tangan :
                            <div style={{ height: 40 }}>&nbsp;</div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
});

export default PrintPlpDocument;
